/**
 * Primary CTA Decision Engine
 *
 * Pure, synchronous. `resolvePrimaryCta` walks the flowchart top-to-bottom over
 * already-fetched supporter data and returns the primary CTA(s) to render.
 *
 * Priority: Delinquency → Departure → Sponsor a Child → Birthday →
 * Christmas → Letter → Photo → Appeal.
 * Departure and Sponsor a Child are hard stops — each returns as the
 * only primary CTA. Special rule: Birthday + Christmas is the only case that
 * returns two CTAs.
 *
 * Every result carries a `monetaryAsk` flag — read by the secondary engine to
 * cap the total monetary asks shown across the primary + secondary CTAs.
 *
 * Time-based branches read the current date internally — no injected clock.
 * Only the result types, input type, and resolver are exported; the decision
 * predicates below are private to this module.
 */
import { PaymentFrequencyType } from '@sola/schema/dotcom/proxy-api/transactions.ts';
import { CommitmentCodeEnum } from '@sola/schema/dotcom/shared/commitments.ts';
import { CommitmentStatusEnum } from '@sola/schema/dotcom/supporters/commitments.ts';
import type {
  GraphQLCommitment,
  GraphQLCommitmentParticipant,
  GraphQLDepartureChild,
  GraphQLDepartureSummary,
  GraphQLGivingHistoryItem,
  GraphQLGivingTotals,
  GraphQLLetter,
  GraphQLPledge,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { GraphQLLetterDirection } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { hasUpcomingBirthday } from '@sola/shared-utils/children/utils.ts';
import {
  filterActiveSponsorshipCommitments,
  filterCurrentSponsorshipCommitments,
  isCommitmentPendingSubmission,
  isSponsorshipCommitmentGiftable,
} from '@sola/shared-utils/supporters/commitments.ts';
import { isActionableDeparture } from '@sola/shared-utils/supporters/departures.ts';
import { isDuringChristmasAppealSeason } from '@sola/shared-utils/supporters/financials.ts';

// ─── Types ─────────────────────────────────────────────────────────────────

export enum PrimaryCtaType {
  Departure = 'departure',
  NoSponsorships = 'no-sponsorships',
  Birthday = 'birthday',
  Christmas = 'christmas',
  UnreadLetters = 'unread-letters',
  NewChildPhoto = 'new-child-photo',
  GeneralAppeal = 'general-appeal',
}

/** Common to every primary CTA result. */
type PrimaryCtaResultBase = {
  /** Does this CTA ask the supporter for money? Read by the secondary engine. */
  monetaryAsk: boolean;
};

type DepartureCtaResult = PrimaryCtaResultBase & {
  type: PrimaryCtaType.Departure;
  participants: GraphQLCommitmentParticipant[];
};

type NoSponsorshipsCtaResult = PrimaryCtaResultBase & {
  type: PrimaryCtaType.NoSponsorships;
};

type BirthdayCtaResult = PrimaryCtaResultBase & {
  type: PrimaryCtaType.Birthday;
  confirmed: boolean;
  participants: GraphQLCommitmentParticipant[];
};

type ChristmasCtaResult = PrimaryCtaResultBase & {
  type: PrimaryCtaType.Christmas;
  sponsorships: GraphQLCommitment[];
};

type UnreadLettersCtaResult = PrimaryCtaResultBase & {
  type: PrimaryCtaType.UnreadLetters;
  letters: GraphQLLetter[];
};

type NewChildPhotoCtaResult = PrimaryCtaResultBase & {
  type: PrimaryCtaType.NewChildPhoto;
  commitments: GraphQLCommitment[];
};

type GeneralAppealCtaResult = PrimaryCtaResultBase & {
  type: PrimaryCtaType.GeneralAppeal;
};

export type PrimaryCtaResult =
  | DepartureCtaResult
  | NoSponsorshipsCtaResult
  | BirthdayCtaResult
  | ChristmasCtaResult
  | UnreadLettersCtaResult
  | NewChildPhotoCtaResult
  | GeneralAppealCtaResult;

export type PrimaryCtaInput = {
  commitments: GraphQLCommitment[];
  pledges: GraphQLPledge[];
  givingTotals: GraphQLGivingTotals | null | undefined;
  departureSummary: GraphQLDepartureSummary | null | undefined;
  lettersAvailable: GraphQLLetter[] | null | undefined;
  recentGiving: GraphQLGivingHistoryItem[] | null | undefined;
  hasNoSponsorshipsContent?: boolean;
};

// ─── Delinquency ─────────────────────────────────────────────────────────────

/** True if the supporter has any missed payment. */
const checkDelinquency = (
  givingTotals: GraphQLGivingTotals | null | undefined,
): boolean => (givingTotals?.totalMissedPaymentAmount ?? 0) > 0;

// ─── Departure ───────────────────────────────────────────────────────────

/**
 * Resolves the departed child's participant record (for name, gender/pronoun,
 * and globalId) by matching the departure against the supporter's commitments.
 * Falls back to a minimal participant built from the departure child so the
 * name still renders even when the commitment is no longer present.
 */
const resolveDepartedParticipant = (
  departedChild: GraphQLDepartureChild,
  commitments: GraphQLCommitment[],
): GraphQLCommitmentParticipant => {
  const match = commitments.find(
    (c) => c.internalId === departedChild.commitmentInternalId,
  )?.participant;

  if (match) {
    return match;
  }

  const name = departedChild.displayName ?? '';
  return {
    globalId: departedChild.globalId,
    name,
    photoUrl: departedChild.imageUrl,
  };
};

/**
 * An actionable departure is a hard-stop primary CTA: when present it is the
 * only CTA rendered.
 */
const checkDeparture = (
  departureSummary: GraphQLDepartureSummary | null | undefined,
  commitments: GraphQLCommitment[],
): PrimaryCtaResult | null => {
  const departures = (departureSummary?.departures ?? []).filter((departure) =>
    isActionableDeparture(departure.effectiveDate),
  );

  if (departures.length === 0) {
    return null;
  }

  const participants = departures.map((departure) =>
    resolveDepartedParticipant(departure.departedChild, commitments),
  );

  return { type: PrimaryCtaType.Departure, monetaryAsk: false, participants };
};

// ─── No Sponsorships ─────────────────────────────────────────────────────────

/**
 * "No Sponsorships" is a hard-stop primary CTA for supporters who aren't
 * currently sponsoring: when it shows, no other primary CTA renders. The
 * secondary engine pairs it with Volunteer and either an active campaign
 * appeal or the Donate fallback.
 */
const checkNoSponsorships = (
  nonDepartureSponsorships: GraphQLCommitment[],
  hasNoSponsorshipsContent: boolean,
): PrimaryCtaResult | null => {
  if (!hasNoSponsorshipsContent) {
    return null;
  }

  // Any kids that aren't currently in departure summary (which shouldn't make it here)
  // Steers toward sponsoring a child → counts as a monetary ask.
  return nonDepartureSponsorships.length > 0
    ? null
    : { type: PrimaryCtaType.NoSponsorships, monetaryAsk: true };
};

// ─── Birthday ────────────────────────────────────────────────────────────────

/**
 * True when the supporter has given this child a birthday gift in the recent
 * giving window.
 *
 * A missing recent-giving payload does not suppress the CTA. The query's
 * bounded lookback window prevents a calendar-year rollover from making a
 * recently given birthday gift appear eligible again.
 */
const hasGivenBirthdayGift = (
  globalId: string | null | undefined,
  recentGiving: GraphQLGivingHistoryItem[] | null | undefined,
): boolean => {
  if (!globalId) {
    return false;
  }

  return (
    recentGiving?.some(
      (giving) =>
        giving.fundCode === CommitmentCodeEnum.BirthdayGift &&
        giving.beneficiaryGlobalId === globalId,
    ) ?? false
  );
};

/**
 * Resolves the birthday CTA variant for children with upcoming birthdays.
 *
 * Birthday eligibility uses the centralized, inclusive three-calendar-month
 * rule in `hasUpcomingBirthday`. This matches the legacy My Account behavior.
 * Children with a recent birthday gift or active birthday-gift commitment see
 * thank-you copy. If any child can still be appealed to, that ask takes
 * precedence and includes only those children.
 */
const checkBirthdayCta = (
  nonDepartureSponsorships: GraphQLCommitment[],
  commitments: GraphQLCommitment[],
  recentGiving: GraphQLGivingHistoryItem[] | null | undefined,
): PrimaryCtaResult | null => {
  const now = new Date();

  const birthdaySponsorships = nonDepartureSponsorships.filter((c) => {
    const birthdate = c.participant?.birthdate;
    return (
      isSponsorshipCommitmentGiftable(c) && hasUpcomingBirthday(birthdate, now)
    );
  });

  if (birthdaySponsorships.length === 0) {
    return null;
  }

  const birthdayCommitmentParticipantIds = new Set(
    commitments
      .filter(
        (commitment) =>
          commitment.fund?.code === CommitmentCodeEnum.BirthdayGift &&
          (commitment.status === CommitmentStatusEnum.Active ||
            isCommitmentPendingSubmission(commitment)),
      )
      .map((commitment) => commitment.participant?.globalId)
      .filter((globalId): globalId is string => !!globalId),
  );

  const [eligibleParticipants, thankYouParticipants] =
    birthdaySponsorships.reduce<
      [GraphQLCommitmentParticipant[], GraphQLCommitmentParticipant[]]
    >(
      ([eligible, thankYou], sponsorship) => {
        const participant =
          sponsorship.participant as GraphQLCommitmentParticipant;
        const isCovered =
          (!!participant.globalId &&
            birthdayCommitmentParticipantIds.has(participant.globalId)) ||
          hasGivenBirthdayGift(participant.globalId, recentGiving);

        (isCovered ? thankYou : eligible).push(participant);
        return [eligible, thankYou];
      },
      [[], []],
    );
  if (eligibleParticipants.length === 0) {
    // Thank-you variant — the gift is already given, so there's no money ask.
    return {
      type: PrimaryCtaType.Birthday,
      monetaryAsk: false,
      confirmed: true,
      participants: thankYouParticipants,
    };
  }

  return {
    type: PrimaryCtaType.Birthday,
    monetaryAsk: true,
    confirmed: false,
    participants: eligibleParticipants,
  };
};

// ─── Christmas appeal ────────────────────────────────────────────────────────

/** Returns a Christmas CTA if the supporter is eligible during the season. */
const checkChristmasEligibility = (
  nonDepartureSponsorships: GraphQLCommitment[],
  commitments: GraphQLCommitment[],
  recentGiving: GraphQLGivingHistoryItem[] | null | undefined,
): PrimaryCtaResult | null => {
  // Read the clock once for the appeal-season check.
  const now = new Date();
  if (!isDuringChristmasAppealSeason(now)) {
    return null;
  }

  // The documented rule is specifically an annual Christmas-gift commitment
  // (not any recurring frequency).
  const hasRecurringChristmas = commitments.some(
    (c) =>
      c.fund?.code === CommitmentCodeEnum.ChristmasGift &&
      (c.status === CommitmentStatusEnum.Active ||
        isCommitmentPendingSubmission(c)) &&
      c.frequency === PaymentFrequencyType.Annually,
  );
  if (hasRecurringChristmas) {
    return null;
  }

  const hasGivenChristmasGift = recentGiving?.some(
    (giving) => giving.fundCode === CommitmentCodeEnum.ChristmasGift,
  );
  if (hasGivenChristmasGift) {
    return null;
  }

  return {
    type: PrimaryCtaType.Christmas,
    monetaryAsk: true,
    sponsorships: nonDepartureSponsorships,
  };
};

// ─── Unread letters ──────────────────────────────────────────────────────────

/**
 * Recency of an inbound letter: when it became available to read, falling back
 * to when it was created. Unparseable dates sort oldest.
 */
const getLetterRecencyMs = (letter: GraphQLLetter): number => {
  const timestamp = Date.parse(letter.readyDate ?? letter.createdDate ?? '');
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getNewestLetterPerBeneficiary = (
  letters: GraphQLLetter[],
): GraphQLLetter[] => {
  const newestFirst = letters.toSorted(
    (a, b) => getLetterRecencyMs(b) - getLetterRecencyMs(a),
  );
  const newestByChild = new Map<string, GraphQLLetter>();

  for (const letter of newestFirst) {
    const childGlobalId = letter.beneficiary?.globalId;
    if (!childGlobalId || newestByChild.has(childGlobalId)) {
      continue;
    }

    newestByChild.set(childGlobalId, letter);
  }

  return [...newestByChild.values()];
};

/**
 *  Returns newest unread inbound letter for each child belonging to a current sponsorship.
 */
const checkNewChildLetter = (
  lettersAvailable: GraphQLLetter[] | null | undefined,
  remainingSponsorships: GraphQLCommitment[],
): PrimaryCtaResult | null => {
  const sponsorshipSet = new Set(
    remainingSponsorships.map((s) => s.participant?.globalId),
  );
  const unreadLetters = lettersAvailable?.filter(
    (l) =>
      l.direction === GraphQLLetterDirection.B2S &&
      !l.isReadBySupporter &&
      l.beneficiary?.globalId &&
      sponsorshipSet.has(l.beneficiary?.globalId),
  );

  if (!unreadLetters?.length) {
    return null;
  }

  return {
    type: PrimaryCtaType.UnreadLetters,
    monetaryAsk: false,
    letters: getNewestLetterPerBeneficiary(unreadLetters),
  };
};

// ─── Placeholders (no service integration yet) ───────────────────────────────

/** Placeholder — no new-child-photo service exists yet. */
const checkNewChildPhoto = (): PrimaryCtaResult | null => {
  // TODO(CTA): Replace with new-child-photo API once available.
  return null;
};

/** Returns the first eligible appeal from the prioritized list. */
const checkGeneralAppeal = (): PrimaryCtaResult | null => {
  // TODO(CTA): Requires prioritized appeal list (not yet integrated).
  return null;
};

// ─── Resolver ────────────────────────────────────────────────────────────────

/** Resolves the primary CTA(s) for the home page. Pure — no side effects. */
export const resolvePrimaryCta = ({
  commitments,
  pledges,
  givingTotals,
  departureSummary,
  lettersAvailable,
  recentGiving,
  hasNoSponsorshipsContent = false,
}: PrimaryCtaInput): PrimaryCtaResult[] => {
  if (checkDelinquency(givingTotals)) {
    return [];
  }

  // Departures are a hard stop: when present, the departure CTA is the only
  // primary CTA rendered.
  const departureCta = checkDeparture(departureSummary, commitments);
  if (departureCta) {
    return [departureCta];
  }

  // Get all sponsorships including transitions for the no sponsorships evaluation
  const remainingSponsorshipsWithTransitions =
    filterCurrentSponsorshipCommitments(commitments);

  const noSponsorshipsCta =
    pledges.length === 0 &&
    checkNoSponsorships(
      remainingSponsorshipsWithTransitions,
      hasNoSponsorshipsContent,
    );
  if (noSponsorshipsCta) {
    return [noSponsorshipsCta];
  }

  // The rest of the CTA logic should exclude transition sponsorships
  const remainingSponsorships = filterActiveSponsorshipCommitments(
    remainingSponsorshipsWithTransitions,
  );

  // No sponsorships/correspondents/pledges
  if (remainingSponsorships.length === 0 && pledges.length === 0) {
    return [];
  }

  const birthdayCta = checkBirthdayCta(
    remainingSponsorships,
    commitments,
    recentGiving,
  );
  const christmasCta = checkChristmasEligibility(
    remainingSponsorships,
    commitments,
    recentGiving,
  );

  if (birthdayCta && christmasCta) {
    return [birthdayCta, christmasCta];
  }
  if (birthdayCta) {
    return [birthdayCta];
  }
  if (christmasCta) {
    return [christmasCta];
  }

  const unreadCta = checkNewChildLetter(
    lettersAvailable,
    remainingSponsorships,
  );
  if (unreadCta) {
    return [unreadCta];
  }

  const photoCta = checkNewChildPhoto();
  if (photoCta) {
    return [photoCta];
  }

  const appealCta = checkGeneralAppeal();
  if (appealCta) {
    return [appealCta];
  }

  return [];
};
